/* React */
import { useState, useEffect, useRef, useCallback, createContext, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

/* Redux */
import { fetchSchedules } from './redux/calendar/calendarScheduleSlice';
import { fetchJobsByQuery, fetchAllJobs } from './redux/jobs/jobSlice';
import { fetchNotifications } from './redux/notifications/notificationSlice';
import { fetchFavorites } from './redux/favorites/favoriteSlice';
import { updateLastseen, clearUser, setPhotoURL, signInGoogleAuthProvider, logOut } from './redux/auth/userSlice';
import { syncPhotoAcrossOrders } from './redux/profiles/profileSlice';
import { persistor } from './redux/store';

/* Firebase */
import { onAuthStateChanged } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from './firebase/firebase';

/* Languages */
import en from './localisation/en.json';
import fi from './localisation/fi.json';
import sv from './localisation/sv.json';

/* Components */
import Footer from './components/Footer';
import Header from './components/Header';
import ScrollToTopButton from './components/elements/ScrollToTopButton';
import ScrollToHash from './components/utils/ScrollToHash';
/* Login modal */
import LoginModal from './pages/LoginModal';
import { useLogin } from './pages/useLogin';
import { LoginModalContext } from './context/LoginModalContext';
import { randomImages } from './components/utils/images';
/* Routes */
import GuestRoute from './routes/GuestRoute';
import UserRoute from './routes/UserRoute';

/* Calendar Pages */
const CalendarSettings    = lazy(() => import('./pages/Calendar/CalendarSettings'));
const ManageSchedules     = lazy(() => import('./pages/Calendar/ManageSchedules'));
const MyCalendar          = lazy(() => import('./pages/Calendar/MyCalendar'));
const Overview            = lazy(() => import('./pages/Calendar/Overview'));
const SellerCalendar      = lazy(() => import('./pages/Calendar/SellerCalendar'));
const SyncCalendar        = lazy(() => import('./pages/Calendar/SyncCalendar'));

/* Infos Pages */
const AdditionalServices  = lazy(() => import('./pages/Infos/AdditionalServices'));
const BusinessSolutions   = lazy(() => import('./pages/Infos/BusinessSolutions'));
const ContactUs           = lazy(() => import('./pages/Infos/ContactUs'));
const FAQsEn              = lazy(() => import('./pages/Infos/FAQsEn'));
const FAQsFi              = lazy(() => import('./pages/Infos/FAQsFi'));
const HowServiceWorks     = lazy(() => import('./pages/Infos/HowServiceWorks'));
const PrivacyPolicy       = lazy(() => import('./pages/Infos/PrivacyPolicy'));
const Pricing             = lazy(() => import('./pages/Infos/Pricing'));
const TermsOfService      = lazy(() => import('./pages/Infos/TermsOfService'));

/* Profile Pages */
const BuyersProfile       = lazy(() => import('./pages/Profiles/buyer/BuyersProfile'));
const BuyerOrders         = lazy(() => import('./pages/Profiles/buyer/BuyerOrders'));
const CreateProfileModal  = lazy(() => import('./pages/Profiles/seller/CreateProfileModal'));
const Dashboard           = lazy(() => import('./pages/Profiles/Dashboard'));
const Messages            = lazy(() => import('./pages/Profiles/Messages'));
const Orders              = lazy(() => import('./pages/Profiles/seller/Orders'));
const OwnJobs             = lazy(() => import('./pages/Profiles/seller/OwnJobs'));
const Ratings             = lazy(() => import('./pages/Profiles/Ratings'));
const SellerProfile       = lazy(() => import('./pages/Profiles/seller/SellerProfile'));
const SellersProfile      = lazy(() => import('./pages/Profiles/seller/SellersProfile'));
const PersonalInfo        = lazy(() => import('./pages/Profiles/seller/PersonalInfo'));
const Settings            = lazy(() => import('./pages/Profiles/Settings'));
const Transactions        = lazy(() => import('./pages/Profiles/seller/Transactions'));
const Favourites          = lazy(() => import('./pages/Profiles/Favourites'));

/* Misc Pages */
const CategoryPage        = lazy(() => import('./pages/CategoryPage'));
const HomePage            = lazy(() => import('./pages/HomePage'));
const RecoverPassword     = lazy(() => import('./pages/RecoverPassword'));
const SearchResultPage    = lazy(() => import('./pages/SearchResultPage'));
const ServiceBookingNEW   = lazy(() => import('./pages/ServiceBooking'));
const ServiceBookingCalendar = lazy(() => import('./pages/ServiceBookingCalendar'));
const UserRegistration    = lazy(() => import('./pages/UserRegistration'));
const VerifyEmail         = lazy(() => import('./pages/VerifyEmail'));
const AboutUsPage         = lazy(() => import('./pages/AboutUsPage'));
const DownloadData        = lazy(() => import('./pages/Profiles/seller/DownloadData'));

/* Style */
import '../src/styles/App.scss';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from './utilities/theme.js';

const Language = createContext();
const languages = { en, fi, sv };
const defaultJob = {
  id: '',
  categories: [],
  cities: [],
  job_title: ''
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function ServiceBookingRoute() {
  const { uid } = useParams();
  return <ServiceBookingNEW key={uid} />;
}

function LoginModalProvider({ children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const returnPathRef = useRef(null);

  const { showLoginModal, setShowLoginModal, usermail, setUsermail, password, setPassword,
    remember, setRemember, randNumber, handleSubmit, loginError, isLoading,
    isEmailNotVerified, handleResendVerification } = useLogin(dispatch, () => {
    navigate(returnPathRef.current || '/');
  });

  const openLoginModal = useCallback((path) => {
    returnPathRef.current = path || window.location.pathname;
    setShowLoginModal(true);
  }, [setShowLoginModal]);

  const handleGoogleLoginClick = async () => {
    try {
      const result = await dispatch(signInGoogleAuthProvider({ remember }));
      if (signInGoogleAuthProvider.fulfilled.match(result)) {
        dispatch(fetchAllJobs());
        setShowLoginModal(false);
        navigate(returnPathRef.current || '/');
      }
    } catch (err) {
      console.error('Google login error:', err);
    }
  };

  return (
    <LoginModalContext.Provider value={openLoginModal}>
      {showLoginModal && (
        <LoginModal
          setShowLoginModal={setShowLoginModal}
          randomImages={randomImages}
          randNumber={randNumber}
          usermail={usermail}
          setUsermail={setUsermail}
          password={password}
          setPassword={setPassword}
          handleSubmit={handleSubmit}
          onGoogleClick={handleGoogleLoginClick}
          remember={remember}
          setRemember={setRemember}
          loginError={loginError}
          isLoading={isLoading}
          isEmailNotVerified={isEmailNotVerified}
          handleResendVerification={handleResendVerification}
        />
      )}
      {children}
    </LoginModalContext.Provider>
  );
}

const App = () => {
  const [lang, setLang] = useState(languages.fi);
  const [jobs, setJobs] = useState([defaultJob]);

  const user = useSelector((state) => state.user);
  const titles = useSelector((state) => state.jobs.titles);
  const dispatch = useDispatch();
  const photoURLRef = useRef(user.photoURL);
  useEffect(() => { photoURLRef.current = user.photoURL; }, [user.photoURL]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        dispatch(clearUser());
        return;
      }
      const rememberUntil = Number(localStorage.getItem('kykyRememberUntil'));
      if (rememberUntil && Date.now() > rememberUntil) {
        localStorage.removeItem('kykyRememberUntil');
        await dispatch(logOut());
        await persistor.purge();
        return;
      }
      // providerData has the Google OAuth photo — not affected by updateProfile()
      const googleProvider = firebaseUser.providerData?.find(p => p.providerId === 'google.com');
      const resolvedPhotoURL = firebaseUser.photoURL || googleProvider?.photoURL || '';
      if (resolvedPhotoURL) {
        dispatch(setPhotoURL(resolvedPhotoURL));
      }
      // Update Firestore only when the stored value has actually changed
      const currentPhotoURL = photoURLRef.current;
      if (resolvedPhotoURL && resolvedPhotoURL !== currentPhotoURL) {
        try {
          await updateDoc(doc(db, 'users', firebaseUser.uid, 'data', 'userdata'), {
            photoURL: resolvedPhotoURL
          });
          dispatch(syncPhotoAcrossOrders({ uid: firebaseUser.uid, photoURL: resolvedPhotoURL }));
        } catch (e) {
          // userdata doc may not exist yet
        }
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user.uid) {
      dispatch(fetchSchedules(user.uid));
      dispatch(fetchJobsByQuery({ key: 'uid', value: user.uid }));
      dispatch(fetchNotifications(user.uid));
      dispatch(fetchFavorites(user.uid));
      dispatch(updateLastseen(user.uid));

      // Update lastseen every 5 minutes while user is active
      const interval = setInterval(() => {
        dispatch(updateLastseen(user.uid));
      }, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user.uid]);

  /* sets job titles for the calendar settings as a parameter
 'cos select button values won't render otherwise */
  useEffect(() => {
    if (titles) {
      setJobs(titles);
    }
  }, [titles]);

  const navlinks = [
    { to: '/', label: 'Home' },
    { to: '/new-profile-creation', label: 'Profile Creation' },
    { to: '/job-creation', label: 'Job Creation' },
    { to: '/buyers-rating', label: 'Buyers Rating' },
    { to: '/contact-form', label: 'Contact Form' }
  ];

  return (
    <div className="appWrapper">
      <ThemeProvider theme={theme}>
        <Language.Provider value={{ lang }}>
          <BrowserRouter>
            <LoginModalProvider>
            <ScrollToTop />
            <ScrollToHash />
            <Header navlinks={navlinks} />
            <ToastContainer
              position="top-right" // Position to display the toast
              autoClose={3000} // Time to auto-close
              hideProgressBar={false} // Show progress bar or not
              newestOnTop={false} // Whether the newest toast will be on top
              closeOnClick // Close toast when clicked
              rtl={false} // Use RTL layout or not
              pauseOnFocusLoss // Pause when losing focus
              draggable // Allow dragging the toast
              pauseOnHover // Pause when hovered
              className="custom-toast" // Add custom class
            />
            {/* Some routes are nested.
        Ex: buyer/dashboard or seller/messages */}
            <Suspense fallback={<div className="page-loading"><div className="page-loading__spinner" /></div>}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="additional-services" element={<AdditionalServices />} />
              <Route path="business-solutions" element={<BusinessSolutions />} />
              <Route path="contact-us" element={<ContactUs />} />
              <Route path="how-service-works" element={<HowServiceWorks />} />
              <Route path="kyky-faqsEn" element={<FAQsEn />} />
              <Route path="categories/:category" element={<CategoryPage />} />
              <Route path="kyky-faqsFi" element={<FAQsFi />} />
              <Route path="about-us" element={<AboutUsPage />} />
              <Route path="user-profile/:slug" element={<SellerProfile />} />
              <Route path="/user-registration" element={<UserRegistration />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route
                path="calendar"
                element={
                  <UserRoute>
                    <MyCalendar />
                  </UserRoute>
                }>
                <Route
                  index
                  element={
                    <UserRoute>
                      <SellerCalendar />
                    </UserRoute>
                  }
                />
                <Route
                  path="overview"
                  element={
                    <UserRoute>
                      <Overview />
                    </UserRoute>
                  }
                />
                <Route
                  path="schedule"
                  element={
                    <UserRoute>
                      <ManageSchedules />
                    </UserRoute>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <UserRoute>
                      <CalendarSettings jobs={jobs} />
                    </UserRoute>
                  }
                />
                <Route
                  path="SyncCalendar"
                  element={
                    <UserRoute>
                      <SyncCalendar />
                    </UserRoute>
                  }
                />
              </Route>{' '}
              {/* Broken Path */}
              <Route path="pricing" element={<Pricing />} />
              <Route path="privacy-policy" element={<PrivacyPolicy />} /> {/* Broken Path */}
              <Route path="search" element={<SearchResultPage />} />
              <Route path="search-result" element={<SearchResultPage />} /> {/* Broken Path */}
              <Route path="service-booking-calendar" element={<ServiceBookingCalendar />} />{' '}
              {/* Broken Path */}
              <Route path="service-booking/:jobId" element={<ServiceBookingRoute />} />
              <Route path="terms-of-service" element={<TermsOfService />} />
              <Route path="downloadData" element={<DownloadData />} />
              <Route
                path="buyer"
                element={
                  <UserRoute>
                    <BuyersProfile />
                  </UserRoute>
                }>
                <Route
                  path="dashboard"
                  element={
                    <UserRoute>
                      <Dashboard />
                    </UserRoute>
                  }
                />
                <Route
                  path="orders"
                  element={
                    <UserRoute>
                      <BuyerOrders />
                    </UserRoute>
                  }
                />
                <Route
                  path="messages"
                  element={
                    <UserRoute>
                      <Messages />
                    </UserRoute>
                  }
                />
                <Route
                  path="review"
                  element={
                    <UserRoute>
                      <Ratings />
                    </UserRoute>
                  }
                />
                <Route
                  path="favorites"
                  element={
                    <UserRoute>
                      <Favourites />
                    </UserRoute>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <UserRoute>
                      <Settings />
                    </UserRoute>
                  }
                />
              </Route>
              <Route
                path="seller"
                element={
                  <UserRoute>
                    <SellersProfile />
                  </UserRoute>
                }>
                <Route
                  path="dashboard"
                  element={
                    <UserRoute>
                      <Dashboard />
                    </UserRoute>
                  }
                />

                <Route
                  path="settings"
                  element={
                    <UserRoute>
                      <Settings />
                    </UserRoute>
                  }
                />

                <Route
                  path="personalInfo"
                  element={
                    <UserRoute>
                      <PersonalInfo />
                    </UserRoute>
                  }
                />
                <Route
                  path="editProfile"
                  element={
                    <UserRoute>
                      <CreateProfileModal />
                    </UserRoute>
                  }
                />
                <Route
                  path="own-jobs"
                  element={
                    <UserRoute>
                      <OwnJobs />
                    </UserRoute>
                  }
                />
                <Route
                  path="orders"
                  element={
                    <UserRoute>
                      <Orders />
                    </UserRoute>
                  }
                />
                <Route
                  path="messages"
                  element={
                    <UserRoute>
                      <Messages />
                    </UserRoute>
                  }
                />
                <Route
                  path="review"
                  element={
                    <UserRoute>
                      <Ratings />
                    </UserRoute>
                  }
                />
                <Route
                  path="favorites"
                  element={
                    <UserRoute>
                      <Favourites />
                    </UserRoute>
                  }
                />
                <Route
                  path="transactions"
                  element={
                    <UserRoute>
                      <Transactions />
                    </UserRoute>
                  }
                />
              </Route>
              <Route path="/user-registration" element={<UserRegistration />} />
              <Route
                path="recover-password"
                element={
                  <GuestRoute>
                    <RecoverPassword />
                  </GuestRoute>
                }
              />
              {/* <Route path="*" element={<Navigate to="/" />} /> */}
            </Routes>
            </Suspense>

            <Footer />
            <ScrollToTopButton />
            </LoginModalProvider>
          </BrowserRouter>
        </Language.Provider>
      </ThemeProvider>
    </div>
  );
};

export default App;


